import React, { ReactNode } from 'react';
import { Button, Modal, Stack, Form, Row, Col } from 'react-bootstrap';
import useUserStore from 'store/user';
import { useCountryListQuery, useUpdateUserDetailsMutation } from 'services';
import useTranslate from 'hooks/useTranslate';
import toast from 'react-hot-toast';
import CustomSearchAddress from 'components/Common/CustomSearchAddress';
import { useFormik } from 'formik';
import { personalInfoInitialValues, personalInfoSchema } from 'constants/schemaValidations';
import { PersonalInfoInterface } from 'constants/interfaces';
import ReactSelect from 'react-select';
import useAuthStore from 'store/auth';
import useCommonStore, { setModalConfig } from 'store/common';
import SearchCompanyList from 'components/Common/SearchCompanyList';
import { getPhoneFormat } from 'utils';

const EditPersonalDetails: React.FC<any> = () => {
	const { translate } = useTranslate();
	const { profileData }: any = useUserStore((state) => state);
	const { modalConfig = {} }: any = useCommonStore((state) => state);
	const { mutateAsync: updateProfile } = useUpdateUserDetailsMutation();
	const { data: countryList = [] } = useCountryListQuery();
	const { user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});

	const onHandleNumberVerify = () => {
		setModalConfig({
			visible: true,
			type: 'verifyMobileNumber',
		});
	};

	const formik: any = useFormik({
		initialValues: {
			...personalInfoInitialValues,
			firstName: profileData?.firstName || '',
			lastName: profileData?.lastName || '',
			designation: profileData?.designation || '',
			cityId: profileData?.city?.id || null,
			stateId: profileData?.state?.id || null,
			countryId: profileData?.country?.id || null,
			address: profileData?.address || '',
			countryCode: profileData?.countryCode || '',
			phoneNumber: profileData?.phoneNumber || '',
			totalExperience: profileData?.totalExperience || 0,
			companyName: profileData?.companyName || '',
			email: profileData?.email || '',
		},
		enableReinitialize: true,
		validationSchema: personalInfoSchema(translate),
		onSubmit: (values: any) => {
			const { email, ...val } = values;
			updateProfile({ ...val, saveType: 'personalDetails' })
				.then((res: any) => {
					if (res?.success) {
						toast.success(res?.message);
					}
				})
				.finally(() => modalConfig?.onClose());
		},
	});
	const renderError = <T extends keyof PersonalInfoInterface>(field: T) =>
		formik.touched[field] && formik.errors[field] ? (
			<span className="text-danger f-error">{formik.errors[field] as ReactNode}</span>
		) : null;

	const handleInputChange = (_value: any) => {
		formik.setFieldValue('address', '');
		formik.setFieldValue('cityId', '');
		formik.setFieldValue('stateId', '');
		formik.setFieldValue('countryId', '');
	};

	const handleChange = (e: any) => {
		const inputValue = getPhoneFormat(e.target.value);
		if (inputValue) {
			formik.setFieldValue('phoneNumber', e.target.value);
		}
	};

	return (
		<Modal.Body>
			<Button
				className="close-btn rounded-circle border-0 fw-bold position-absolute"
				onClick={() => modalConfig?.onClose()}
			>
				<i className="ri-close-line"></i>
			</Button>
			<Form className="edit-form" onSubmit={formik.handleSubmit}>
				<Stack className="gap-4">
					<h4 className="fw-bold mb-0">{translate('Profile.editPersonalDetails')}</h4>
					<Row>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Common.firstNameCommon')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterFirstName')}
									className="rounded-pill"
									name="firstName"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.firstName}
									autoComplete="off"
								/>
								{renderError('firstName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Common.lastNameCommon')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterLastName')}
									className="rounded-pill"
									name="lastName"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.lastName}
									autoComplete="off"
								/>
								{renderError('lastName')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">
									{translate('Profile.experienceCompanyName')}
								</Form.Label>
								<SearchCompanyList
									selectProps={{
										placeholder: `${translate('PlaceHolder.enterCompanyName')}`,
										onChange: (value: any) => {
											formik.setFieldValue('companyName', value.companyName);
										},
										onCreateOption: (value: any) => {
											formik.setFieldValue('companyName', value);
										},
										name: 'companyName',
									}}
									fieldValue={formik.values.companyName}
									onBlur={formik.handleBlur}
									formik={formik}
								/>
								{renderError('companyName')}
							</Form.Group>
						</Col>

						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Profile.editEmail')}</Form.Label>
								<Form.Control
									disabled
									type="text"
									placeholder={translate('PlaceHolder.editEmail')}
									className="rounded-pill"
									name="email"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.email}
									autoComplete="off"
								/>
								{renderError('email')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('Common.designation')}</Form.Label>
								<Form.Control
									type="text"
									placeholder={translate('PlaceHolder.enterdesignation')}
									className="rounded-pill"
									name="designation"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.designation}
									autoComplete="off"
								/>
								{renderError('designation')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative custom-search-address">
								<Form.Label className="lh-1">{translate('PlaceHolder.location')}</Form.Label>
								<CustomSearchAddress
									selectProps={{
										placeholder: translate('PlaceHolder.locationText'),
										onChange: (value: any) => {
											formik.setFieldValue('address', value.location);
											formik.setFieldValue('cityId', value.cityId);
											formik.setFieldValue('stateId', value.stateId);
											formik.setFieldValue('countryId', value.countryId);
										},
										name: 'address',
									}}
									type="profileLocation"
									handleInputChange={handleInputChange}
									fieldValue={formik.values.address}
									onBlur={formik.handleBlur}
									formik={formik}
								/>
								<i className="ri-search-line form-icon position-absolute lh-1"></i>
								{renderError('address') ||
									renderError('cityId') ||
									renderError('stateId') ||
									renderError('countryId')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.phone')}</Form.Label>
								<Button
									variant="link"
									className="border-0 p-0 ms-3 verify-number-link"
									disabled={user?.isPhoneNumberVerified || !profileData?.phoneNumber}
									onClick={() => (!user?.isPhoneNumberVerified ? onHandleNumberVerify() : '')}
								>
									{user?.isPhoneNumberVerified
										? translate('Common.verified')
										: translate('Common.verifyNumber')}
								</Button>
								<div className="number-input d-flex">
									<ReactSelect
										className="form-react-select"
										classNamePrefix="select"
										isSearchable
										name="countryCode"
										autoFocus={false}
										options={countryList}
										getOptionLabel={(option: any) => option?.phonecode}
										getOptionValue={(option: any) => option?.phonecode}
										onChange={(value: any) => {
											formik.setFieldValue('countryCode', value?.phonecode);
										}}
										value={countryList?.find(
											(item: any) => item.phonecode === formik.values.countryCode,
										)}
										onBlur={formik.handleBlur}
									/>
									<Form.Control
										type="text"
										placeholder={translate('PlaceHolder.phone')}
										name="phoneNumber"
										className="rounded-pill flex-1"
										onChange={handleChange}
										onBlur={formik.handleBlur}
										value={formik.values.phoneNumber}
										autoComplete="off"
										maxLength={12}
									/>
								</div>
								{renderError('countryCode') || renderError('phoneNumber')}
							</Form.Group>
						</Col>
						<Col md="6">
							<Form.Group className="mb-4 position-relative">
								<Form.Label className="lh-1">{translate('PlaceHolder.experience')}</Form.Label>
								<Form.Control
									type="number"
									placeholder={translate('PlaceHolder.experience')}
									className="rounded-pill"
									name="totalExperience"
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									value={formik.values.totalExperience}
									autoComplete="off"
									min={0}
								/>
								{renderError('totalExperience')}
							</Form.Group>
						</Col>
					</Row>
					<div className="edit-modal-actions d-flex gap-4">
						<Button className="filled-btn rounded-pill" type="submit">
							{translate('Common.save')}
						</Button>
					</div>
				</Stack>
			</Form>
		</Modal.Body>
	);
};

export default EditPersonalDetails;
